import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type {
  GuideAudience,
  GuideCategory,
  Risk,
} from "@/guides/adminGuides.types";
import {
  ADMIN_GUIDES,
  getAdminGuideById,
  getGuideAudiences,
  getGuideCategories,
  getGuideRisks,
} from "@/guides/adminGuidesRegistry";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { VNextCard, VNextNotice, VNextStat } from "@/vnext/primitives";
import { GuideDetailCard } from "@/vnext/routes/settings/guides/GuideDetailCard";
import { GuidesCatalogCard } from "@/vnext/routes/settings/guides/GuidesCatalogCard";
import {
  ADMIN_GUIDES_ROUTE_BASE,
  countActiveGuideFilters,
  filterAdminGuides,
  type GuidesFilters,
  toggleSelection,
} from "@/vnext/routes/settings/guides/guidesRouteModel";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";

import "@/vnext/routes/settings/guides/guidesMake.css";

const logger = FrontendLoggerFactory.getLogger().withComponent("GuidesRoute");

const DEFAULT_FILTERS: GuidesFilters = {
  audiences: [],
  categories: [],
  risks: [],
  search: "",
  showNotSupportedYet: false,
};

export default function GuidesRoute() {
  const navigate = useNavigate();
  const { guideId } = useParams<{ guideId?: string }>();
  const [filters, setFilters] = useState<GuidesFilters>(DEFAULT_FILTERS);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  const selectedGuide = guideId ? getAdminGuideById(guideId) : undefined;
  const invalidGuideId = guideId && !selectedGuide ? guideId : undefined;
  const categories = useMemo(() => getGuideCategories(), []);
  const audiences = useMemo(() => getGuideAudiences(), []);
  const risks = useMemo(() => getGuideRisks(), []);
  const filteredGuides = useMemo(
    () => filterAdminGuides(ADMIN_GUIDES, filters),
    [filters],
  );
  const activeFilterCount = useMemo(
    () => countActiveGuideFilters(filters),
    [filters],
  );

  useEffect(() => {
    logger.debug("GUIDES_ROUTE_ENTER", {
      component: "GuidesRoute",
      feature: "ADMIN",
      operation: "GUIDES_ROUTE_ENTER",
    });
  }, []);

  useEffect(() => {
    if (!guideId || invalidGuideId) {
      return;
    }

    logger.debug("GUIDES_ROUTE_GUIDE_VIEW", {
      component: "GuidesRoute",
      feature: "ADMIN",
      guideId,
      operation: "GUIDES_ROUTE_GUIDE_VIEW",
    });
  }, [guideId, invalidGuideId]);

  const setSearch = (search: string) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      search,
    }));
  };

  const handleToggleCategory = (category: GuideCategory) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      categories: toggleSelection(currentFilters.categories, category),
    }));
  };

  const handleToggleAudience = (audience: GuideAudience) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      audiences: toggleSelection(currentFilters.audiences, audience),
    }));
  };

  const handleToggleRisk = (risk: Risk) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      risks: toggleSelection(currentFilters.risks, risk),
    }));
  };

  const handleToggleShowNotSupportedYet = () => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      showNotSupportedYet: !currentFilters.showNotSupportedYet,
    }));
  };

  const handleSelectGuide = (nextGuideId: string) => {
    void navigate(`${ADMIN_GUIDES_ROUTE_BASE}/${nextGuideId}`);
  };

  const handleBrowseAll = () => {
    void navigate(ADMIN_GUIDES_ROUTE_BASE);
  };

  const handleCopyLink = async () => {
    if (!selectedGuide) {
      return;
    }

    const clipboard = (
      navigator as Omit<Navigator, "clipboard"> & {
        clipboard?: Partial<Clipboard>;
      }
    ).clipboard;

    if (typeof clipboard?.writeText !== "function") {
      setFeedbackMessage(
        "Clipboard is unavailable in this browser. Copy the URL from the address bar instead.",
      );
      logger.debug("GUIDES_ROUTE_COPY_UNAVAILABLE", {
        component: "GuidesRoute",
        feature: "ADMIN",
        operation: "GUIDES_ROUTE_COPY_UNAVAILABLE",
      });
      return;
    }

    const url = `${window.location.origin}${ADMIN_GUIDES_ROUTE_BASE}/${selectedGuide.id}`;

    try {
      await clipboard.writeText(url);
      setFeedbackMessage("Guide link copied.");
    } catch {
      setFeedbackMessage(
        "Guide link could not be copied. Copy the URL from the address bar instead.",
      );
      logger.debug("GUIDES_ROUTE_COPY_FAIL", {
        component: "GuidesRoute",
        feature: "ADMIN",
        guideId: selectedGuide.id,
        operation: "GUIDES_ROUTE_COPY_FAIL",
      });
    }
  };

  return (
    <SettingsWorkspaceChrome
      subtitle="Search internal operator guides, safety notes, and linked admin workflows from one place."
      title="Guides"
    >
      <div className="guides-route">
        {feedbackMessage ? (
          <VNextNotice tone="info">{feedbackMessage}</VNextNotice>
        ) : null}

        <VNextNotice tone="info">
          Use this library as an operator playbook. Open a guide to follow the
          steps, then jump straight to the linked admin page when you need to
          act.
        </VNextNotice>

        <div className="guides-route__stats">
          <VNextStat
            detail="Registry-backed internal guides"
            label="Total guides"
            value={ADMIN_GUIDES.length}
          />
          <VNextStat
            detail="After search and filters"
            label="Matching guides"
            value={filteredGuides.length}
          />
          <VNextStat
            detail="Category, audience, risk, and support-state filters"
            label="Active filters"
            value={activeFilterCount}
          />
        </div>

        <div className="guides-route__layout">
          <GuidesCatalogCard
            audiences={audiences}
            categories={categories}
            filteredGuides={filteredGuides}
            onSearchChange={setSearch}
            onSelectGuide={handleSelectGuide}
            onToggleAudience={handleToggleAudience}
            onToggleCategory={handleToggleCategory}
            onToggleRisk={handleToggleRisk}
            onToggleShowNotSupportedYet={handleToggleShowNotSupportedYet}
            resultCount={filteredGuides.length}
            risks={risks}
            search={filters.search}
            selectedAudiences={filters.audiences}
            selectedCategories={filters.categories}
            selectedGuideId={selectedGuide?.id}
            selectedRisks={filters.risks}
            showNotSupportedYet={filters.showNotSupportedYet}
            totalCount={ADMIN_GUIDES.length}
          />

          <GuideDetailCard
            invalidGuideId={invalidGuideId}
            onBrowseAll={handleBrowseAll}
            onCopyLink={handleCopyLink}
            selectedGuide={selectedGuide}
          />
        </div>

        {invalidGuideId ? (
          <VNextCard
            className="guides-route__detail-card"
            title="Guide ids stay stable"
          >
            <p>
              If an internal doc or bookmark still points to an older path,
              update it to the clean first-class route under{" "}
              <code>{ADMIN_GUIDES_ROUTE_BASE}</code>.
            </p>
          </VNextCard>
        ) : null}
      </div>
    </SettingsWorkspaceChrome>
  );
}
